import { onMounted, onUnmounted, ref, type Ref } from 'vue';

/**
 * Détecte si le viewport est « mobile » (sous un breakpoint) via matchMedia,
 * de façon réactive et SSR-safe. Factorise le pattern matchMedia dupliqué
 * inline (BuildingManagementShell, BuildingCard, AdminLayout).
 *
 * @param maxWidth largeur max (incluse) considérée mobile. Défaut 767.99 =
 *   breakpoint $bp-md du design (cf _variables.scss) ; passer 1023.99 pour le
 *   seuil tablette.
 * @returns ref booléen réactif (true = viewport <= maxWidth).
 *
 * @example
 * const isMobile = useIsMobile();
 * // template : <el-table v-if="!isMobile" ... /> <div v-else class="mobile-cards">…</div>
 */
export function useIsMobile(maxWidth = 767.99): Ref<boolean> {
  const isMobile = ref(false);
  let mql: MediaQueryList | null = null;

  function onChange(e: MediaQueryListEvent | MediaQueryList): void {
    isMobile.value = e.matches;
  }

  onMounted(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    mql = window.matchMedia(`(max-width: ${maxWidth}px)`);
    isMobile.value = mql.matches;
    mql.addEventListener('change', onChange);
  });

  onUnmounted(() => {
    if (mql) mql.removeEventListener('change', onChange);
  });

  return isMobile;
}
